import { ApolloClient, ApolloProvider, createHttpLink, InMemoryCache } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { GlobalStyles } from '@m/alchemy-ui/GlobalStyles';
import { ThemeProvider } from '@m/alchemy-ui/theme';
import fetch from 'cross-fetch';
import { enableES5 } from 'immer';
import { ErrorBoundary } from 'react-error-boundary';
import { DialogProvider, IntlProvider, PageMetadataProvider, RuntimeProvider } from '../context';
import { getCommunityTheme } from './theme';
import { type AppProps } from './interfaces';
import { ErrorFallback } from './components';
import { CrowdicityGlobalStyle } from './CrowdicityGlobalStyle';
import { GRAPHQL_URI } from './constants';

enableES5();

const createApolloClient = ({ sessionId, sessionTimeout }: Pick<AppProps, 'sessionId' | 'sessionTimeout'>) => {
  // The session token is never exposed to JS: it travels as the httpOnly
  // user_pkey cookie, served same-origin via the /graphql proxy.
  const authLink = setContext((_, { headers }) => ({
    headers: {
      ...headers,
      'x-crowdicity-session-id': sessionId,
      'x-crowdicity-session-timeout': sessionTimeout
    }
  }));
  return new ApolloClient({
    cache: new InMemoryCache({
      typePolicies: {
        User: {
          fields: {
            socialLinks: {
              merge: false
            }
          }
        }
      }
    }),
    link: authLink.concat(
      createHttpLink({
        fetch,
        uri: GRAPHQL_URI,
        credentials: 'include'
      })
    )
  });
};

export const AdmWrapper = (props: AppProps) => {
  const apiV2Url = String(props.apiV2Url);
  const sessionId = String(props.sessionId) || '0';
  const sessionTimeout = String(props.sessionTimeout) || '0';
  const locale = props.locale || 'en-GB';

  const handleError = (error: Error) => {
    console.log(error);
  };

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback} onError={handleError}>
      <RuntimeProvider
        value={{
          ...props,
          apiV2Url,
          sessionId,
          sessionTimeout
        }}
      >
        <ApolloProvider
          client={createApolloClient({
            sessionId,
            sessionTimeout
          })}
        >
          <PageMetadataProvider>
            <ThemeProvider
              theme={getCommunityTheme({
                communityColors: props.community.colors
              })}
            >
              <IntlProvider locale={locale}>
                <DialogProvider>
                  <GlobalStyles globalCss={CrowdicityGlobalStyle} />
                  {props.children}
                </DialogProvider>
              </IntlProvider>
            </ThemeProvider>
          </PageMetadataProvider>
        </ApolloProvider>
      </RuntimeProvider>
    </ErrorBoundary>
  );
};
